<template>
  <div class="flex flex-col">
    <transition name="modal">
      <confirmation-modal
        v-if="confirmationModal"
        :message="confirmationModal.message"
        :loading="loadingOrder || loadingGenerateOrders"
        @close="confirmationModal = null"
        @confirm="confirmAction(confirmationModal.action, confirmationModal.order)"
      ></confirmation-modal>
    </transition>

    <h2>Órdenes</h2>

    <custom-search
      class="mt-2 w-full sm:w-[500px]"
      placeholder="Buscar por No. de Orden, Cliente ó Mascota"
      v-model="searchText"
      @confirm="search"
      @clear="search"
    />

    <div class="flex flex-wrap mt-2 items-center space-x-2">
      <button
        :class="[
          'bg-white p-2 rounded font-medium mt-2 w-fit shadow-primary',
          { 'text-content-300': !enableStatusFilters },
          { 'text-primary-500': enableStatusFilters },
        ]"
        @click="changeStatusFilter"
      >
        Filtro de estatus
      </button>

      <button
        :class="[
          'bg-white p-2 rounded font-medium mt-2 w-fit shadow-primary',
          { 'text-content-300': petTypeFilter !== 'dog' },
          { 'text-primary-500': petTypeFilter === 'dog' },
        ]"
        @click="changePetTypeFilter('dog')"
      >
        Perros
      </button>

      <button
        :class="[
          'bg-white p-2 rounded font-medium mt-2 w-fit shadow-primary',
          { 'text-content-300': petTypeFilter !== 'cat' },
          { 'text-primary-500': petTypeFilter === 'cat' },
        ]"
        @click="changePetTypeFilter('cat')"
      >
        Gatos
      </button>

      <button
        :class="[
          'bg-white p-2 rounded font-medium mt-2 w-fit shadow-primary',
          { 'text-content-300': orderType !== 'desc' },
          { 'text-primary-500': orderType === 'desc' },
        ]"
        @click="changeOrderTypeFilter"
      >
        Últimas arriba
      </button>
    </div>

    <div class="flex flex-col lg:flex-row gap-4 lg:gap-6 mt-4 items-start">
      <status-side-nav
        v-if="enableStatusFilters"
        :options="statusNavOptions"
      />

      <div class="flex-1 min-w-0 w-full">

    <div v-if="hasPermission('edit_orders')">
      <button
        class="secondary-button mx-0"
        @click.prevent="showGenerateOrders = !showGenerateOrders"
      >
        Generar órdenes
      </button>

      <div v-if="showGenerateOrders" class="card mt-2 p-4 w-full sm:w-[500px]">
        <p class="text-sm text-content-300 mb-2">
          Busca por nombre de cliente o mascota para generar órdenes.
        </p>
        <div class="flex items-center space-x-2">
          <input
            v-model="generateSearchText"
            type="text"
            placeholder="Nombre de cliente o mascota"
            class="input w-full"
            @keyup.enter="searchOrderCycles"
          />
          <primary-button
            :loading="loadingGenerateSearch"
            @buttonClicked="searchOrderCycles"
            :disabled="!generateSearchText || generateSearchText.length < 2"
            class="mx-0 whitespace-nowrap"
          >
            Buscar
          </primary-button>
        </div>

        <div v-if="orderCycleResults && orderCycleResults.length === 0" class="mt-3">
          <p class="text-sm text-content-300">No se encontraron ciclos de orden.</p>
        </div>

        <div v-if="orderCycleResults && orderCycleResults.length > 0" class="mt-3 space-y-2">
          <div
            v-for="cycle in orderCycleResults"
            :key="cycle.id"
            class="bg-gray-50 rounded p-3"
          >
            <div class="flex items-center justify-between">
              <div>
                <p class="font-medium text-sm">{{ cycle.client_name }}</p>
                <p class="text-xs text-content-300">
                  {{ cycle.pets.join(', ') }} &mdash;
                  {{ cycle.cycle_count }}/{{ cycle.total_cycles }} órdenes generadas
                  &mdash; {{ cycle.cycle_duration }} semana(s) por orden
                </p>
              </div>
            </div>
            <div class="flex items-center space-x-2 mt-2">
              <input
                v-model.number="cycle.generateCount"
                type="number"
                min="1"
                max="12"
                placeholder="Cantidad"
                class="input w-24"
              />
              <primary-button
                :loading="loadingGenerateOrders && generateOrderCycleId === cycle.id"
                @buttonClicked="
                  generateOrderCycleId = cycle.id;
                  generateOrderCount = cycle.generateCount || 1;
                  generateDeductBudget = cycle.deductBudget !== false;
                  confirmationModal = {
                    action: 'generateOrders',
                    message: '¿Generar ' + (cycle.generateCount || 1) + ' orden(es) para ' + cycle.client_name + ' (' + cycle.pets.join(', ') + ')?' + (cycle.deductBudget === false ? ' NOTA: NO se deducirá del saldo del cliente (cortesía/reemplazo).' : ' Se deducirá el costo de estas órdenes del saldo del cliente.'),
                  }
                "
                :disabled="!cycle.generateCount || cycle.generateCount < 1"
                class="mx-0 whitespace-nowrap text-sm"
              >
                Generar
              </primary-button>
            </div>
            <!-- Toggle: deducir del saldo (default true).
                 Destildar SOLO si estas órdenes son una cortesía o reemplazan una orden
                 ya cobrada que se perdió/canceló por error. Ver: agent_docs/bugfixes/saldo-fantasma-generate-orders.md -->
            <label class="flex items-center mt-2 text-xs text-content-300 cursor-pointer">
              <input
                type="checkbox"
                :checked="cycle.deductBudget !== false"
                @change="cycle.deductBudget = $event.target.checked"
                class="mr-2"
              />
              <span>
                Deducir costo del saldo del cliente
                <span class="text-content-500">(destildar solo para cortesías o reemplazos)</span>
              </span>
            </label>
          </div>
        </div>
      </div>
    </div>

    <div v-if="!loading && orders" class="flex flex-col">
      <div v-if="status === 'pending'">
        <p class="mt-6" v-if="orders.length === 0">No hay órdenes pendientes</p>

        <pending-order
          v-for="order in orders"
          :key="order.id"
          :order="order"
          @updateMealSet="mealSetToUpdate = $event"
          class="card mt-6 w-full"
        >
          <primary-button
            v-if="canProcessOrderOrder(order) && hasPermission('edit_orders')"
            :loading="loadingOrder"
            @buttonClicked="
              confirmationModal = {
                action: 'processOrder',
                order: order,
                message:
                  '¿Estás seguro que deseas procesar e imprimir la orden?',
              }
            "
            class="mt-4 mx-0"
            >Procesar e Imprimir Orden
          </primary-button>

          <button
            v-if="canProcessOrderOrder(order) && hasPermission('edit_orders')"
            class="secondary-button mt-4 mx-0"
            @click.prevent="
              confirmationModal = {
                action: 'processOrderWithoutPrint',
                order: order,
                message:
                  '¿Estás seguro que deseas procesar la orden SIN imprimir?',
              }
            "
          >
            Procesar SIN Imprimir
          </button>

          <button
            class="mt-4 delete-button"
            @click.prevent="
              confirmationModal = {
                action: 'cancelOrder',
                order: order,
                message:
                  '¿Estás seguro que deseas cancelar la orden? Deberás de comunicarlo con el adminstrador para aplicar el reembolso correspondiente.',
              }
            "
          >
            Cancelar Orden
          </button>

          <div v-if="hasPermission('edit_orders')" class="mt-4">
            <div class="flex items-center space-x-2">
              <select v-model="orderNewStatuses[order.id]" class="input text-sm">
                <option :value="undefined">Cambiar estatus...</option>
                <option v-for="s in ['pending', 'in_process', 'ready_for_pickup', 'ready_for_delivery', 'delivered', 'canceled']" :key="s" :value="s" :disabled="s === order.status">{{ getStatusLabel(s) }}</option>
              </select>
              <primary-button
                :loading="loadingOrder"
                @buttonClicked="confirmationModal = { action: 'changeOrderStatus', order: order, message: '¿Cambiar estatus de la orden #' + order.id + ' a ' + getStatusLabel(orderNewStatuses[order.id]) + '?' }"
                :disabled="!orderNewStatuses[order.id]"
                class="mx-0 whitespace-nowrap text-sm"
              >Cambiar</primary-button>
            </div>
          </div>
        </pending-order>
      </div>

      <div v-else-if="status === 'in_process'">
        <p class="mt-6" v-if="orders.length === 0">No hay órdenes en proceso</p>

        <in-process-order
          v-for="order in orders"
          :key="order.id"
          :order="order"
          class="mt-6 w-full"
        >
          <primary-button
            v-if="hasPermission('edit_orders')"
            :loading="loadingOrder"
            @buttonClicked="
              confirmationModal = {
                action: 'completeOrder',
                order: order,
                message: '¿Estás seguro que deseas completar la orden?',
              }
            "
            class="mt-4 mx-0"
            >Completar Orden</primary-button
          >

          <div v-if="hasPermission('edit_orders')" class="mt-4">
            <div class="flex items-center space-x-2">
              <select v-model="orderNewStatuses[order.id]" class="input text-sm">
                <option :value="undefined">Cambiar estatus...</option>
                <option v-for="s in ['pending', 'in_process', 'ready_for_pickup', 'ready_for_delivery', 'delivered', 'canceled']" :key="s" :value="s" :disabled="s === order.status">{{ getStatusLabel(s) }}</option>
              </select>
              <primary-button
                :loading="loadingOrder"
                @buttonClicked="confirmationModal = { action: 'changeOrderStatus', order: order, message: '¿Cambiar estatus de la orden #' + order.id + ' a ' + getStatusLabel(orderNewStatuses[order.id]) + '?' }"
                :disabled="!orderNewStatuses[order.id]"
                class="mx-0 whitespace-nowrap text-sm"
              >Cambiar</primary-button>
            </div>
          </div>
        </in-process-order>
      </div>

      <div v-else-if="status === 'ready_for_pickup'">
        <p class="mt-6" v-if="orders.length === 0">
          No hay órdenes listas para Pick-Up
        </p>

        <ready-for-pickup-order
          v-for="order in orders"
          :key="order.id"
          :order="order"
          class="mt-6 w-full"
        >
          <primary-button
            v-if="hasPermission('edit_orders')"
            :loading="loadingOrder"
            @buttonClicked="
              confirmationModal = {
                action: 'deliverOrder',
                order: order,
                message: '¿Estás seguro que deseas entregar la orden?',
              }
            "
            class="mt-4 mx-0"
            >Entregar Orden</primary-button
          >

          <div v-if="hasPermission('edit_orders')" class="mt-4">
            <div class="flex items-center space-x-2">
              <select v-model="orderNewStatuses[order.id]" class="input text-sm">
                <option :value="undefined">Cambiar estatus...</option>
                <option v-for="s in ['pending', 'in_process', 'ready_for_pickup', 'ready_for_delivery', 'delivered', 'canceled']" :key="s" :value="s" :disabled="s === order.status">{{ getStatusLabel(s) }}</option>
              </select>
              <primary-button
                :loading="loadingOrder"
                @buttonClicked="confirmationModal = { action: 'changeOrderStatus', order: order, message: '¿Cambiar estatus de la orden #' + order.id + ' a ' + getStatusLabel(orderNewStatuses[order.id]) + '?' }"
                :disabled="!orderNewStatuses[order.id]"
                class="mx-0 whitespace-nowrap text-sm"
              >Cambiar</primary-button>
            </div>
          </div>
        </ready-for-pickup-order>
      </div>

      <div v-else-if="status === 'ready_for_delivery'">
        <p class="mt-6" v-if="orders.length === 0">
          No hay órdenes listas para entrega
        </p>

        <ready-for-delivery-order
          v-for="order in orders"
          :key="order.id"
          :order="order"
          class="mt-6 w-full"
        >
          <div
            v-if="
              order.status === 'ready_for_delivery' &&
              isValidForShipping(order) &&
              hasPermission('edit_orders')
            "
          >
            <p v-if="!order.shippingPrice" class="mt-4">
              Debes asignar un precio de envío ya que se encuentra fuera del
              rango local.
            </p>

            <button
              @click.prevent="orderToSetShippingPrice = order"
              class="secondary-button mt-4 mx-0"
            >
              {{ order.shippingPrice ? "Cambiar" : "Agregar" }} precio de envío
            </button>

            <div v-if="order.shippingPrice">
              <primary-button
                :loading="loadingOrder"
                @buttonClicked="validateSendOrder(order)"
                class="mt-4 mx-0"
                >Enviar Orden por
                {{ integerToCurrency(order.shippingPrice) }}</primary-button
              >
            </div>
          </div>

          <div v-if="hasPermission('edit_orders')" class="mt-4">
            <div class="flex items-center space-x-2">
              <select v-model="orderNewStatuses[order.id]" class="input text-sm">
                <option :value="undefined">Cambiar estatus...</option>
                <option v-for="s in ['pending', 'in_process', 'ready_for_pickup', 'ready_for_delivery', 'delivered', 'canceled']" :key="s" :value="s" :disabled="s === order.status">{{ getStatusLabel(s) }}</option>
              </select>
              <primary-button
                :loading="loadingOrder"
                @buttonClicked="confirmationModal = { action: 'changeOrderStatus', order: order, message: '¿Cambiar estatus de la orden #' + order.id + ' a ' + getStatusLabel(orderNewStatuses[order.id]) + '?' }"
                :disabled="!orderNewStatuses[order.id]"
                class="mx-0 whitespace-nowrap text-sm"
              >Cambiar</primary-button>
            </div>
          </div>
        </ready-for-delivery-order>
      </div>

      <div v-else-if="status === 'delivered'">
        <p class="mt-6" v-if="orders.length === 0">
          No hay órdenes entregadas / enviadas
        </p>

        <delivered-order
          v-for="order in orders"
          :key="order.id"
          :order="order"
          @orderFailed="
            confirmationModal = {
              action: 'failedToSend',
              order: order,
              message:
                '¿Estás seguro que deseas seleccionar esta opción? El cliente deberá seleccionar nuevamente el método de envío para esta orden.',
            }
          "
          @updateTrackingInfo="loadOrders"
          class="mt-6 w-full"
        >
          <div v-if="hasPermission('edit_orders')" class="mt-4">
            <div class="flex items-center space-x-2">
              <select v-model="orderNewStatuses[order.id]" class="input text-sm">
                <option :value="undefined">Cambiar estatus...</option>
                <option v-for="s in ['pending', 'in_process', 'ready_for_pickup', 'ready_for_delivery', 'delivered', 'canceled']" :key="s" :value="s" :disabled="s === order.status">{{ getStatusLabel(s) }}</option>
              </select>
              <primary-button
                :loading="loadingOrder"
                @buttonClicked="confirmationModal = { action: 'changeOrderStatus', order: order, message: '¿Cambiar estatus de la orden #' + order.id + ' a ' + getStatusLabel(orderNewStatuses[order.id]) + '?' }"
                :disabled="!orderNewStatuses[order.id]"
                class="mx-0 whitespace-nowrap text-sm"
              >Cambiar</primary-button>
            </div>
          </div>
        </delivered-order>
      </div>

      <div v-else-if="status === 'failed_to_send'">
        <p class="mt-6" v-if="orders.length === 0">
          No hay órdenes fallidas al enviar
        </p>

        <failed-to-send-order
          v-for="order in orders"
          :key="order.id"
          :order="order"
          class="mt-6 w-full"
        >
          <div v-if="hasPermission('edit_orders')" class="mt-4">
            <div class="flex items-center space-x-2">
              <select v-model="orderNewStatuses[order.id]" class="input text-sm">
                <option :value="undefined">Cambiar estatus...</option>
                <option v-for="s in ['pending', 'in_process', 'ready_for_pickup', 'ready_for_delivery', 'delivered', 'canceled']" :key="s" :value="s" :disabled="s === order.status">{{ getStatusLabel(s) }}</option>
              </select>
              <primary-button
                :loading="loadingOrder"
                @buttonClicked="confirmationModal = { action: 'changeOrderStatus', order: order, message: '¿Cambiar estatus de la orden #' + order.id + ' a ' + getStatusLabel(orderNewStatuses[order.id]) + '?' }"
                :disabled="!orderNewStatuses[order.id]"
                class="mx-0 whitespace-nowrap text-sm"
              >Cambiar</primary-button>
            </div>
          </div>
        </failed-to-send-order>
      </div>

      <div v-else-if="!enableStatusFilters">
        <p class="mt-6" v-if="orders.length === 0">
          No hay órdenes encontradas
        </p>

        <general-order
          v-for="order in orders"
          :key="order.id"
          :order="order"
          @orderFailed="
            confirmationModal = {
              action: 'failedToSend',
              order: order,
              message:
                '¿Estás seguro que deseas seleccionar esta opción? El cliente deberá seleccionar nuevamente el método de envío para esta orden.',
            }
          "
          @updateTrackingInfo="loadOrders"
          class="mt-6 w-full"
        >
          <primary-button
            v-if="
              order.status === 'pending' &&
              canProcessOrderOrder(order) &&
              hasPermission('edit_orders')
            "
            :loading="loadingOrder"
            @buttonClicked="
              confirmationModal = {
                action: 'processOrder',
                order: order,
                message:
                  '¿Estás seguro que deseas procesar e imprimir la orden?',
              }
            "
            class="mt-4 mx-0"
            >Procesar e Imprimir Orden
          </primary-button>

          <button
            v-if="
              order.status === 'pending' &&
              canProcessOrderOrder(order) &&
              hasPermission('edit_orders')
            "
            class="secondary-button mt-4 mx-0"
            @click.prevent="
              confirmationModal = {
                action: 'processOrderWithoutPrint',
                order: order,
                message:
                  '¿Estás seguro que deseas procesar la orden SIN imprimir?',
              }
            "
          >
            Procesar SIN Imprimir
          </button>

          <primary-button
            v-else-if="
              order.status === 'in_process' && hasPermission('edit_orders')
            "
            :loading="loadingOrder"
            @buttonClicked="
              confirmationModal = {
                action: 'completeOrder',
                order: order,
                message: '¿Estás seguro que deseas completar la orden?',
              }
            "
            class="mt-4 mx-0"
            >Completar Orden</primary-button
          >

          <primary-button
            v-if="
              order.status === 'ready_for_pickup' &&
              hasPermission('edit_orders')
            "
            :loading="loadingOrder"
            @buttonClicked="
              confirmationModal = {
                action: 'deliverOrder',
                order: order,
                message: '¿Estás seguro que deseas entregar la orden?',
              }
            "
            class="mt-4 mx-0"
            >Entregar Orden</primary-button
          >

          <div
            v-if="
              order.status === 'ready_for_delivery' &&
              isValidForShipping(order) &&
              hasPermission('edit_orders')
            "
          >
            <p v-if="!order.shippingPrice" class="mt-4">
              Debes asignar un precio de envío ya que se encuentra fuera del
              rango local.
            </p>

            <button
              @click.prevent="orderToSetShippingPrice = order"
              class="secondary-button mt-4 mx-0"
            >
              {{ order.shippingPrice ? "Cambiar" : "Agregar" }} precio de envío
            </button>

            <div v-if="order.shippingPrice">
              <primary-button
                :loading="loadingOrder"
                @buttonClicked="validateSendOrder(order)"
                class="mt-4 mx-0"
                >Enviar Orden por
                {{ integerToCurrency(order.shippingPrice) }}</primary-button
              >
            </div>
          </div>

          <div v-if="hasPermission('edit_orders')" class="mt-4">
            <div class="flex items-center space-x-2">
              <select v-model="orderNewStatuses[order.id]" class="input text-sm">
                <option :value="undefined">Cambiar estatus...</option>
                <option v-for="s in ['pending', 'in_process', 'ready_for_pickup', 'ready_for_delivery', 'delivered', 'canceled']" :key="s" :value="s" :disabled="s === order.status">{{ getStatusLabel(s) }}</option>
              </select>
              <primary-button
                :loading="loadingOrder"
                @buttonClicked="confirmationModal = { action: 'changeOrderStatus', order: order, message: '¿Cambiar estatus de la orden #' + order.id + ' a ' + getStatusLabel(orderNewStatuses[order.id]) + '?' }"
                :disabled="!orderNewStatuses[order.id]"
                class="mx-0 whitespace-nowrap text-sm"
              >Cambiar</primary-button>
            </div>
          </div>
        </general-order>
      </div>

      <pagination
        :current-page="currentPage"
        :total-pages="totalPages"
        @page-change="onPageChange"
      />
    </div>

    <div v-else class="flex w-full h-full items-center">
      <loader-icon class="h-10 w-10 text-primary-500 mx-auto"></loader-icon>
    </div>
      </div>
    </div>
  </div>
</template>

<script>
import PendingOrder from "@/components/orders/Pending";
import InProcessOrder from "@/components/orders/InProcess";
import ReadyForPickupOrder from "@/components/orders/ReadyForPickup";
import ReadyForDeliveryOrder from "@/components/orders/ReadyForDelivery";
import DeliveredOrder from "@/components/orders/Delivered";
import FailedToSendOrder from "@/components/orders/FailedToSend";
import GeneralOrder from "@/components/orders/General";
import ConfirmationModal from "@/components/ui/ConfirmationModal";
import Pagination from "@/components/ui/Pagination.vue";
import StatusSideNav from "@/components/ui/StatusSideNav";
import Diet from "@/components/models/Diet";
import MealSet from "@/components/models/MealSet";
import api from "@/api";
import { integerToCurrency } from "@/utils/currencyFormatter";
export default {
  name: "Orders",

  components: {
    StatusSideNav,
    PendingOrder,
    InProcessOrder,
    ReadyForPickupOrder,
    ReadyForDeliveryOrder,
    DeliveredOrder,
    FailedToSendOrder,
    GeneralOrder,
    ConfirmationModal,
    Pagination,
    Diet,
    MealSet,
  },

  data() {
    return {
      orders: null,
      currentPage: 1,
      totalPages: 0,
      loading: false,
      loadingOrder: false,
      mealSetToUpdate: null,
      alertModalMessage: null,
      confirmationModal: null,
      searchText: null,
      status: null,
      enableStatusFilters: true,
      petTypeFilter: null,
      orderType: null,
      shippingPrice: null,
      orderToSetShippingPrice: null,
      carriers: null,
      orderNewStatuses: {},
      showGenerateOrders: false,
      generateSearchText: null,
      generateOrderCycleId: null,
      generateOrderCount: 1,
      // CAMBIO 2026-04-23: default true = deducir saldo. Ver backend OrderController::generateOrders.
      generateDeductBudget: true,
      orderCycleResults: null,
      loadingGenerateSearch: false,
      loadingGenerateOrders: false,
    };
  },

  created() {
    this.$store.commit("setSelectedTab", "orders");

    this.status = this.$route.query.status;
    this.searchText = this.$route.query.search;
    this.petTypeFilter = this.$route.query.petType;
    this.orderType = this.$route.query.orderType;
    this.enableStatusFilters = this.status != null && this.status != "";

    // this.loadDietCategories();
    // this.loadDietStages();
    // this.loadDietTypes();
    this.loadOrders();
    // this.loadCarriers();
  },

  computed: {
    statusNavOptions() {
      const query = (status) => ({
        status,
        search: this.searchText,
        petType: this.petTypeFilter,
        orderType: this.orderType,
      });

      return [
        { status: "pending", label: "Pendientes", icon: "🕐", activeClass: "text-pending" },
        { status: "in_process", label: "En Proceso", icon: "⚙️", activeClass: "text-inProcess" },
        { status: "ready_for_pickup", label: "Listas para Pick-Up", icon: "🛍️", activeClass: "text-ready" },
        { status: "ready_for_delivery", label: "Listas para Enviar", icon: "📦", activeClass: "text-ready" },
        { status: "delivered", label: "Entregadas / Enviadas", icon: "✅", activeClass: "text-delivered" },
        { status: "failed_to_send", label: "Fallidas al Enviar", icon: "⚠️", activeClass: "text-failed" },
      ].map((option) => ({
        label: option.label,
        icon: option.icon,
        to: { name: "ordersIndex", query: query(option.status) },
        active: this.status === option.status,
        activeClass: option.activeClass,
      }));
    },
  },

  methods: {
    integerToCurrency,

    async loadOrders() {
      this.loading = true;

      try {
        const response = await api.orders.getAllWithQueryParams([
          {
            name: "status",
            value: this.status,
          },
          {
            name: "page",
            value: this.currentPage,
          },
          {
            name: "search",
            value: this.searchText,
          },
          {
            name: "petType",
            value: this.petTypeFilter,
          },
          {
            name: "orderType",
            value: this.orderType,
          },
        ]);
        this.orders = response.data;
        this.currentPage = response.current_page;
        this.totalPages = response.last_page;
      } catch (error) {
        this.$store.dispatch("errorSnack", error?.response?.data?.message);
      }

      this.loading = false;
    },

    async loadCarriers() {
      try {
        this.carriers = await api.resources.getCarriers();
      } catch (error) {
        this.$store.dispatch("errorSnack", error?.response?.data?.message);
      }
    },

    search() {
      this.currentPage = 1;
      this.$router.push({
        name: "ordersIndex",
        query: {
          status: this.status,
          search: this.searchText,
          petType: this.petTypeFilter,
          orderType: this.orderType,
        },
      });
      this.loadOrders();
    },

    changeStatusFilter() {
      this.enableStatusFilters = !this.enableStatusFilters;

      this.$router.push({
        name: "ordersIndex",
        query: {
          status: this.enableStatusFilters ? "pending" : "",
          search: this.searchText,
          petType: this.petTypeFilter,
          orderType: this.orderType,
        },
      });
    },

    changePetTypeFilter(petType) {
      if (petType === "dog") {
        if (this.petTypeFilter === "dog") {
          this.petTypeFilter = null;
        } else {
          this.petTypeFilter = "dog";
        }
      } else {
        if (this.petTypeFilter === "cat") {
          this.petTypeFilter = null;
        } else {
          this.petTypeFilter = "cat";
        }
      }

      this.$router.push({
        name: "ordersIndex",
        query: {
          status: this.status,
          search: this.searchText,
          petType: this.petTypeFilter,
          orderType: this.orderType,
        },
      });
    },

    changeOrderTypeFilter() {
      if (this.orderType === "asc") {
        this.orderType = "desc";
      } else if (this.orderType === "desc") {
        this.orderType = "asc";
      } else {
        this.orderType = "desc";
      }

      this.$router.push({
        name: "ordersIndex",
        query: {
          status: this.status,
          search: this.searchText,
          petType: this.petTypeFilter,
          orderType: this.orderType,
        },
      });
    },

    closeMealSetForm() {
      this.mealSetToUpdate = null;
    },

    mealSetUpdated() {
      this.mealSetToUpdate = null;
      this.loadOrders();
    },

    canProcessOrderOrder(order) {
      if (!order.summary) {
        return false;
      }

      for (let cat of order.summary) {
        for (let subcat of cat.subcategories) {
          if (!subcat.available) {
            return false;
          }
        }
      }

      return true;
    },

    isValidForShipping(order) {
      const branch = order.address.branches.filter(
        (br) => br.id === order.branch_id
      )[0];

      return branch.pivot.valid_for_shipping;
    },

    confirmAction(action, order) {
      this[action](order);
      this.confirmationModal = null;
    },

    async processOrder(order) {
      this.loadingOrder = true;

      try {
        const response = await api.orders.processOrder(order.id, {
          summary: order.summary,
        });
        this.$store.dispatch("successSnack", response.message);
        this.loadOrders();
      } catch (error) {
        this.$store.dispatch("errorSnack", error?.response?.data?.message);
      }

      this.loadingOrder = false;
    },

    async processOrderWithoutPrint(order) {
      this.loadingOrder = true;

      try {
        const response = await api.orders.processOrder(order.id, {
          summary: order.summary,
          skip_print: true,
        });
        this.$store.dispatch("successSnack", response.message);
        this.loadOrders();
      } catch (error) {
        this.$store.dispatch("errorSnack", error?.response?.data?.message);
      }

      this.loadingOrder = false;
    },

    async completeOrder(order) {
      this.loadingOrder = true;

      try {
        const response = await api.orders.completeOrder(order.id);
        this.$store.dispatch("successSnack", response.message);
        this.loadOrders();
      } catch (error) {
        this.$store.dispatch("errorSnack", error?.response?.data?.message);
      }

      this.loadingOrder = false;
    },

    validateSendOrder(order) {
      if (
        !order.related_shipment_orders.every(
          (or) => or.status === "ready_for_delivery"
        )
      ) {
        this.alertModalMessage = `Esta orden debe enviarse con más órdenes, por lo tanto todas deben de estar en 'Listas para Enviar'.`;
        return;
      }

      this.confirmationModal = {
        action: "sendOrder",
        order: order,
        message:
          "¿Estás seguro que deseas enviar esta order por " +
          this.integerToCurrency(order.shippingPrice) +
          "?",
      };
    },

    async sendOrder(order) {
      this.loadingOrder = true;

      const request = {
        shipping_price: order.shippingPrice,
      };

      try {
        const response = await api.orders.sendOrder(order.id, request);
        this.$store.dispatch("successSnack", response.message);
        this.loadOrders();
      } catch (error) {
        this.$store.dispatch("errorSnack", error?.response?.data?.message);
      }

      this.loadingOrder = false;
    },

    async deliverOrder(order) {
      this.loadingOrder = true;

      try {
        const response = await api.orders.deliverOrder(order.id);
        this.$store.dispatch("successSnack", response.message);
        this.loadOrders();
      } catch (error) {
        this.$store.dispatch("errorSnack", error?.response?.data?.message);
      }

      this.loadingOrder = false;
    },

    async failedToSend(order) {
      this.loadingOrder = true;

      try {
        const response = await api.orders.failedToSend(order.id);
        this.$store.dispatch("successSnack", response.message);
        this.loadOrders();
      } catch (error) {
        this.$store.dispatch("errorSnack", error?.response?.data?.message);
      }

      this.loadingOrder = false;
    },

    async cancelOrder(order) {
      this.loadingOrder = true;

      try {
        const response = await api.orders.cancelOrder(order.id);
        this.$store.dispatch("successSnack", response.message);
        this.loadOrders();
      } catch (error) {
        this.$store.dispatch("errorSnack", error?.response?.data?.message);
      }

      this.loadingOrder = false;
    },

    onPageChange(page) {
      this.currentPage = page;
      this.loadOrders();
    },

    async searchOrderCycles() {
      if (!this.generateSearchText || this.generateSearchText.length < 2) return;

      this.loadingGenerateSearch = true;
      this.orderCycleResults = null;

      try {
        this.orderCycleResults = await api.orders.searchOrderCycles(this.generateSearchText);
      } catch (error) {
        this.$store.dispatch("errorSnack", error?.response?.data?.message);
      }

      this.loadingGenerateSearch = false;
    },

    getStatusLabel(status) {
      const labels = {
        pending: 'Pendiente',
        in_process: 'En Proceso',
        ready_for_pickup: 'Lista para Pick-Up',
        ready_for_delivery: 'Lista para Enviar',
        delivered: 'Entregada',
        canceled: 'Cancelada',
      };
      return labels[status] || status;
    },

    async changeOrderStatus(order) {
      const newStatus = this.orderNewStatuses[order.id];
      if (!newStatus) return;

      this.loadingOrder = true;

      try {
        const response = await api.orders.changeStatus(order.id, { status: newStatus });
        this.$store.dispatch("successSnack", response.message);
        this.orderNewStatuses[order.id] = null;
        this.loadOrders();
      } catch (error) {
        this.$store.dispatch("errorSnack", error?.response?.data?.message);
      }

      this.loadingOrder = false;
    },

    async generateOrders() {
      this.loadingGenerateOrders = true;

      try {
        // CAMBIO 2026-04-23: pasamos generateDeductBudget (default true).
        // Si el admin destildó el checkbox de "Deducir del saldo", se pasa false
        // (solo para cortesías o reemplazos de órdenes ya cobradas).
        const response = await api.orders.generateOrders(
          this.generateOrderCycleId,
          this.generateOrderCount,
          this.generateDeductBudget
        );
        this.$store.dispatch("successSnack", response.message);
        this.generateOrderCycleId = null;
        this.generateOrderCount = 1;
        this.generateDeductBudget = true;
        this.orderCycleResults = null;
        this.generateSearchText = null;
        this.showGenerateOrders = false;
        this.loadOrders();
      } catch (error) {
        this.$store.dispatch("errorSnack", error?.response?.data?.message);
      }

      this.loadingGenerateOrders = false;
    },
  },

  watch: {
    "$route.query": {
      immediate: true,
      handler(newQuery, oldQuery) {
        this.status = newQuery.status;
        this.petTypeFilter = newQuery.petType;
        this.orderType = newQuery.orderType;

        this.loadOrders();
      },
    },
  },
};
</script>